/* SMD factory engraving, v1. ES2019; no dependencies or persistent storage.
 * Cart pairing is a storefront convenience, NOT server-side price enforcement.
 * Read README.md before installing. Public integration API: window.SMDEngraving.
 */
(function (global) {
  'use strict';
  const STYLES = { caps: 'ALL CAPS', title: 'First Letters Capitalized', written: 'As Written' };
  const own = (object, key) => Object.prototype.hasOwnProperty.call(object || {}, key);
  const clone = value => JSON.parse(JSON.stringify(value));
  const canonical = value => {
    if (Array.isArray(value)) return '[' + value.map(canonical).join(',') + ']';
    if (value && typeof value === 'object') return '{' + Object.keys(value).sort().map(k => JSON.stringify(k) + ':' + canonical(value[k])).join(',') + '}';
    return JSON.stringify(value);
  };
  const length = value => Array.from(value || '').length;
  function styled(text, style) {
    if (style === 'caps') return text.toUpperCase();
    if (style === 'title') return text.toLowerCase().replace(/(^|[\s\-/])([^\s\-/])/g, (all, a, b) => a + b.toUpperCase());
    return text;
  }
  function configFor(catalog, sku) {
    if (!own(catalog.skus, sku)) return null;
    const item = catalog.skus[sku];
    if (!item.enabled || !own(catalog.profiles, item.profile)) return null;
    const result = Object.assign({}, catalog.profiles[item.profile], item, { sku: sku });
    if (result.white_only && result.color !== 'White') return null;
    if (result.family === 'decoflex' && (!result.positions || !result.positions.length)) return null;
    return result;
  }
  function fieldsFor(cfg, layout) {
    if (cfg.family === 'decoflex') return cfg.positions.map(key => ({
      key: key, label: 'Button ' + key, max: /[GH]/.test(key) ? 5 : 9,
      locked: (cfg.locked_positions || []).indexOf(key) !== -1,
      default: (cfg.factory_labels || {})[key] || '', logo: false
    }));
    const fields = [];
    const line = (key, label) => fields.push({ key: key, label: label, max: 16, logo: true, default: '' });
    const buttons = prefix => ['1', '2', '3', '4'].forEach(n => fields.push({ key: prefix + 'Button' + n, label: (prefix ? prefix + ' ' : '') + 'Button ' + n, max: 5, default: '' }));
    if (layout === 'horizontal') {
      ['Left', 'Right'].forEach(side => { line(side + 'Top', side + ' Frame Top'); line(side + 'Bottom', side + ' Frame Bottom'); buttons(side); });
    } else if (layout === 'vertical') {
      line('Top', 'Frame Top'); line('Middle', 'Frame Middle'); line('Bottom', 'Frame Bottom'); buttons('Upper'); buttons('Lower');
    } else {
      line('Top', 'Frame Top'); line('Bottom', 'Frame Bottom');
      if (layout === 'single4') buttons('');
    }
    return fields;
  }
  function initialState(cfg) {
    const layout = cfg.family === 'smoove' ? cfg.layouts[0] : cfg.mount;
    const values = {};
    fieldsFor(cfg, layout).forEach(f => { values[f.key] = { mode: 'text', text: f.default }; });
    return { layout: layout, color: cfg.color, style: 'caps', fields: values, context: {} };
  }
  function makeSpec(cfg, state) {
    const fields = {};
    fieldsFor(cfg, state.layout).forEach(f => {
      const value = state.fields[f.key] || { mode: 'text', text: f.default };
      if (value.mode === 'logo') fields[f.key] = { mode: 'logo', file: value.file ? clone(value.file) : null };
      else fields[f.key] = { mode: 'text', text: f.locked ? f.default : styled(value.text || f.default, state.style) };
    });
    const context = {};
    ['Project', 'Area', 'Room', 'Installer', 'Purchase Order'].forEach(k => { if (state.context[k]) context[k] = state.context[k]; });
    return { version: 1, sku: cfg.sku, family: cfg.family, form: cfg.form, layout: state.layout, color: cfg.color, style: state.style, fields: fields, context: context };
  }
  function billable(cfg, spec) {
    let count = 0;
    fieldsFor(cfg, spec.layout).forEach(f => {
      const v = spec.fields[f.key];
      if (!v || f.locked) return;
      if (v.mode === 'logo' && v.file) count += 1;
      if (v.mode === 'text' && v.text && v.text !== f.default) count += 1;
    });
    return cfg.pricing.mode === 'flat' ? (count > 0 ? 1 : 0) : count;
  }
  function validate(cfg, spec, quantity, maxQuantity) {
    const errors = [];
    if (!own(STYLES, spec.style)) errors.push('Choose a character style.');
    if (!Number.isInteger(quantity) || quantity < 1 || quantity > maxQuantity) errors.push('Enter a quantity from 1 to ' + maxQuantity + '.');
    if (cfg.family === 'smoove' && cfg.layouts.indexOf(spec.layout) === -1) errors.push('Choose a supported frame layout.');
    if (spec.color !== cfg.color) errors.push('Button color must match the purchased product.');
    fieldsFor(cfg, spec.layout).forEach(f => {
      const v = spec.fields[f.key];
      if (!v) { errors.push(f.label + ' is missing.'); return; }
      if (v.mode === 'logo') {
        if (!f.logo || !v.file || !/\.dxf$/i.test(v.file.name || '') || !v.file.sha256) errors.push(f.label + ': select a .dxf logo file.');
      } else if (v.mode !== 'text') errors.push(f.label + ': choose text or logo.');
      else {
        if (length(v.text) > f.max) errors.push(f.label + ': maximum ' + f.max + ' characters, including spaces.');
        if (/[\u0000-\u001f\u007f]/.test(v.text)) errors.push(f.label + ': enter a single line of text.');
        if (f.locked && v.text !== f.default) errors.push(f.label + ' must remain ' + f.default + '.');
      }
    });
    if (!billable(cfg, spec)) errors.push('Add at least one custom label or logo.');
    return errors;
  }
  function resolveFee(runtime, cfg) {
    const variants = runtime.fee_variants || [];
    const fee = variants.find(v => cfg.pricing.fee_variant_id ? String(v.id) === String(cfg.pricing.fee_variant_id) : v.title === cfg.pricing.fee_variant_title);
    if (!fee || !fee.available || Math.round(cfg.pricing.unit_price * 100) !== Number(fee.price)) return null;
    if (runtime.currency !== runtime.catalog.currency) return null;
    return fee;
  }
  function feeProperties(ref, sku, variantId) {
    return { _engraving_ref: ref, _engraving_role: 'fee', _parent_sku: sku, _parent_variant: String(variantId) };
  }
  // Compute expectations from parent specs and the catalog, never from a fee line's claimed quantity.
  function feePlan(cart, runtime) {
    const expected = new Map();
    const fees = [];
    cart.items.forEach(item => {
      const p = item.properties || {};
      if (!p._engraving_ref) return;
      if (p._engraving_role === 'fee') { fees.push(item); return; }
      if (p._engraving_role !== 'parent') throw new Error('An engraving line needs review. Remove it and customize the product again.');
      let spec;
      try { spec = JSON.parse(p._engraving_spec); } catch (e) { throw new Error('An engraving specification is unreadable. Remove that item and customize it again.'); }
      const cfg = configFor(runtime.catalog, spec.sku);
      if (!cfg) throw new Error('This engraving option is no longer available. Remove that item before checkout.');
      const problems = validate(cfg, spec, item.quantity, runtime.catalog.max_quantity);
      const fee = resolveFee(runtime, cfg);
      if (problems.length || !fee || String(p._sku) !== cfg.sku || (item.sku && item.sku !== cfg.sku)) throw new Error('An engraving item needs its configuration or fee reviewed before checkout.');
      const key = p._engraving_ref;
      const units = billable(cfg, spec) * item.quantity;
      if (expected.has(key)) {
        const previous = expected.get(key);
        if (String(previous.id) !== String(fee.id)) throw new Error('Conflicting engraving fees.');
        previous.quantity += units;
      } else expected.set(key, { id: fee.id, quantity: units, properties: feeProperties(key, cfg.sku, item.variant_id || item.id) });
    });
    const updates = {};
    const groups = new Map();
    fees.forEach(item => {
      const ref = item.properties._engraving_ref, match = expected.get(ref);
      if (!match || String(match.id) !== String(item.variant_id || item.id)) updates[item.key] = 0;
      else { if (!groups.has(ref)) groups.set(ref, []); groups.get(ref).push(item); }
    });
    groups.forEach((items, ref) => {
      const match = expected.get(ref); match.claimed = true;
      if (items.reduce((sum, item) => sum + item.quantity, 0) !== match.quantity) {
        updates[items[0].key] = match.quantity;
        items.slice(1).forEach(item => { updates[item.key] = 0; });
      }
    });
    const additions = Array.from(expected.values()).filter(v => !v.claimed).map(v => ({ id: v.id, quantity: v.quantity, properties: v.properties }));
    return { updates: updates, additions: additions };
  }
  const core = { canonical: canonical, styled: styled, configFor: configFor, fieldsFor: fieldsFor, initialState: initialState, makeSpec: makeSpec, billable: billable, validate: validate, resolveFee: resolveFee, feePlan: feePlan };
  if (typeof module !== 'undefined' && module.exports) module.exports = core;
  if (typeof document === 'undefined') return;
  if (global.SMDEngraving) { global.SMDEngraving.init(); return; }

  const nativeFetch = global.fetch.bind(global);
  const roots = new Set();
  let runtime = null, active = null, queue = Promise.resolve(), pending = 0, scheduled = null;
  const externalWrites = new Set();
  const url = path => ((global.Shopify && global.Shopify.routes && global.Shopify.routes.root) || '/').replace(/\/?$/, '/') + path;
  function emit(name, detail) { document.dispatchEvent(new CustomEvent(name, { detail: detail })); }
  function enqueue(fn) {
    pending += 1;
    const result = queue.then(fn);
    queue = result.catch(() => {}).then(() => { pending -= 1; });
    return result;
  }
  async function api(path, body) {
    const options = { credentials: 'same-origin', headers: { Accept: 'application/json' } };
    if (body !== undefined) {
      options.method = 'POST';
      if (body instanceof FormData) options.body = body;
      else { options.headers['Content-Type'] = 'application/json'; options.body = JSON.stringify(body); }
    }
    const response = await nativeFetch(url(path), options);
    let data;
    try { data = await response.json(); } catch (e) { throw new Error('The cart response could not be read. Check your cart before trying again.'); }
    if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : (data.message || 'The cart could not be updated.'));
    return data;
  }
  function notice(message) {
    let node = document.querySelector('[data-smd-cart-notice]');
    if (!node) {
      node = document.createElement('div'); node.className = 'smd-cart-notice'; node.dataset.smdCartNotice = ''; node.setAttribute('role', 'alert');
      document.body.appendChild(node);
    }
    node.textContent = message; node.hidden = !message;
  }
  async function reconcile() {
    if (!runtime) return null;
    await Promise.all(Array.from(externalWrites).map(write => write.catch(() => undefined)));
    let cart = await api('cart.js');
    // A bounded recheck deals with partial failures and Shopify discount splits.
    for (let attempt = 0; attempt < 3; attempt += 1) {
      const plan = feePlan(cart, runtime);
      if (!Object.keys(plan.updates).length && !plan.additions.length) { notice(''); return cart; }
      if (Object.keys(plan.updates).length) cart = await api('cart/update.js', { updates: plan.updates });
      if (plan.additions.length) { await api('cart/add.js', { items: plan.additions }); cart = await api('cart.js'); }
    }
    const left = feePlan(cart, runtime);
    if (Object.keys(left.updates).length || left.additions.length) throw new Error('Engraving fees could not be synchronized. Review your cart before checkout.');
    return cart;
  }
  async function refresh(cart, open) {
    emit('smd:cart-updated', { cart: cart });
    if (apiPublic.cartAdapter && typeof apiPublic.cartAdapter.refresh === 'function') {
      try { await apiPublic.cartAdapter.refresh(cart, open); return; } catch (e) { console.warn('[SMD] Theme cart adapter failed.', e); }
    }
    // Dawn-style drawers are supported only when both rendering methods are present.
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.getSectionsToRender === 'function' && typeof drawer.renderContents === 'function') {
      try {
        const ids = drawer.getSectionsToRender().map(s => s.id).slice(0, 5);
        const response = await nativeFetch(url('cart') + '?sections=' + encodeURIComponent(ids.join(',')), { credentials: 'same-origin' });
        const sections = await response.json();
        if (!response.ok || ids.some(id => !sections[id])) throw new Error('Missing cart section.');
        drawer.renderContents(Object.assign({}, cart, { sections: sections }));
        if (open && typeof drawer.open === 'function') drawer.open();
        return;
      } catch (e) { console.warn('[SMD] Cart section refresh unavailable.', e); }
    }
    if (open) global.location.assign(url('cart'));
  }
  function scheduleSync() {
    if (scheduled) clearTimeout(scheduled);
    scheduled = setTimeout(() => {
      scheduled = null;
      enqueue(async () => { const cart = await reconcile(); await refresh(cart, false); }).catch(e => notice(e.message));
    }, 100);
  }
  function installGuard() {
    // Observe successful theme fetch mutations without rewriting theme responses.
    // Explicit theme adapters should also dispatch smd:cart-changed for XHR-based carts.
    global.fetch = function (input, options) {
      const requestURL = typeof input === 'string' ? input : input.url;
      let isCart = false;
      try { const parsed = new URL(requestURL, global.location.href); isCart = parsed.origin === global.location.origin && /\/cart\/(add|change|update|clear)(\.js)?$/.test(parsed.pathname); } catch (e) {}
      const result = nativeFetch(input, options);
      if (isCart) {
        externalWrites.add(result);
        result.then(() => { externalWrites.delete(result); scheduleSync(); }, () => { externalWrites.delete(result); scheduleSync(); });
      }
      return result;
    };
    document.addEventListener('smd:cart-changed', scheduleSync);
    document.addEventListener('cart:updated', scheduleSync);
    global.addEventListener('pageshow', scheduleSync);
    global.addEventListener('focus', scheduleSync);
    document.addEventListener('submit', event => {
      const form = event.target;
      const checkout = event.submitter && event.submitter.name === 'checkout';
      if (!checkout && !/\/checkout(?:\?|$)/.test(form.getAttribute('action') || '')) return;
      event.preventDefault(); event.stopImmediatePropagation();
      checkoutGuard();
    }, true);
    document.addEventListener('click', event => {
      const node = event.target.closest('a[href],button[name="checkout"],input[name="checkout"],[data-smd-checkout]');
      if (!node) return;
      let checkout = node.name === 'checkout' || node.hasAttribute('data-smd-checkout');
      if (node.tagName === 'A') { try { checkout = /\/checkout\/?$/.test(new URL(node.href).pathname); } catch (e) {} }
      if (!checkout) return;
      event.preventDefault(); event.stopImmediatePropagation(); checkoutGuard();
    }, true);
  }
  let checkingOut = false;
  function checkoutGuard() {
    if (checkingOut) return;
    checkingOut = true;
    enqueue(async () => { await reconcile(); global.location.assign(url('checkout')); })
      .catch(e => notice(e.message)).finally(() => { checkingOut = false; });
  }
  async function digest(bytes) {
    if (!global.crypto || !global.crypto.subtle) throw new Error('Customization needs a secure HTTPS page.');
    const hash = await global.crypto.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(hash)).map(v => v.toString(16).padStart(2, '0')).join('');
  }
  function readFile(file) {
    return new Promise((resolve, reject) => { const reader = new FileReader(); reader.onload = () => resolve(reader.result); reader.onerror = () => reject(new Error('The logo file could not be read.')); reader.readAsArrayBuffer(file); });
  }
  function el(tag, attrs, text) {
    const n = document.createElement(tag);
    Object.keys(attrs || {}).forEach(k => n.setAttribute(k, attrs[k]));
    if (text !== undefined) n.textContent = text;
    return n;
  }
  function inputLabel(parent, label, input) {
    const wrap = el('label', { class: 'smd-label' }); wrap.appendChild(el('span', {}, label)); wrap.appendChild(input); parent.appendChild(wrap); return wrap;
  }
  const svgNS = 'http://www.w3.org/2000/svg';
  function svgNode(tag, attrs, text) {
    const n = document.createElementNS(svgNS, tag); Object.keys(attrs || {}).forEach(k => n.setAttribute(k, attrs[k])); if (text !== undefined) n.textContent = text; return n;
  }
  let serial = 0;
  function drawPreview(container, cfg, spec) {
    container.textContent = '';
    const svg = svgNode('svg', { viewBox: spec.layout === 'horizontal' ? '0 0 460 320' : '0 0 320 460', role: 'img', 'aria-label': 'Live ' + cfg.name + ' engraving preview' });
    const defs = svgNode('defs'); svg.appendChild(defs);
    const black = cfg.color === 'Black', fill = black ? '#303033' : cfg.color === 'Ivory' ? '#eee6d4' : '#fafaf8';
    const ink = black ? '#ffffff' : '#333535', edge = black ? '#535356' : '#cececa';
    let clipIndex = 0;
    function rect(x, y, w, h, r, color) { svg.appendChild(svgNode('rect', { x: x, y: y, width: w, height: h, rx: r, fill: color || fill, stroke: edge, 'stroke-width': 1 })); }
    function text(x, y, value, size) { svg.appendChild(svgNode('text', { x: x, y: y, 'text-anchor': 'middle', 'dominant-baseline': 'central', fill: ink, 'font-family': 'Arial, sans-serif', 'font-size': size || 10, 'font-weight': 600 }, value)); }
    function label(key, x, y, w, h, button) {
      const v = spec.fields[key] || {}, value = v.mode === 'logo' ? 'LOGO' : (v.text || '');
      if (button) rect(x, y, w, h, 2);
      const id = 'smd-clip-' + serial + '-' + (++clipIndex);
      const clip = svgNode('clipPath', { id: id }); clip.appendChild(svgNode('rect', { x: x + 4, y: y + 2, width: w - 8, height: h - 4 })); defs.appendChild(clip);
      const node = svgNode('text', { x: x + w / 2, y: y + h / 2, 'text-anchor': 'middle', 'dominant-baseline': 'central', fill: ink, 'font-family': 'Arial, sans-serif', 'font-size': w < 70 ? 9 : 11, 'font-weight': 600, 'clip-path': 'url(#' + id + ')' }, value);
      svg.appendChild(node);
    }
    if (cfg.family === 'decoflex') {
      const surface = cfg.mount === 'surface';
      rect(surface ? 66 : 45, 15, surface ? 188 : 230, 428, surface ? 16 : 8);
      if (!surface) [43, 417].forEach(y => { svg.appendChild(svgNode('circle', { cx: 160, cy: y, r: 7, fill: fill, stroke: edge })); svg.appendChild(svgNode('path', { d: 'M154 ' + y + 'h12', stroke: edge })); });
      rect(85, 76, 150, 310, 8);
      const rows = cfg.positions.filter(p => p !== 'G' && p !== 'H');
      const rowHeight = Math.min(36, 216 / rows.length), start = 100 + (6 - rows.length) * 13;
      rows.forEach((p, i) => { const y = start + i * rowHeight; label(p, 109, y, 108, rowHeight - 2, true); if (p !== 'F') svg.appendChild(svgNode('circle', { cx: 99, cy: y + rowHeight / 2, r: 2.4, fill: i === 0 ? '#d7a900' : '#686867' })); });
      const y = start + rows.length * rowHeight;
      if (cfg.positions.indexOf('G') !== -1) label('G', 109, y, 53, 37, true);
      if (cfg.positions.indexOf('H') !== -1) label('H', 163, y, 54, 37, true);
      text(161, 370, 'somfy', 15);
    } else {
      function face(x, y, prefix, four) {
        rect(x, y, 128, 126, 4, '#fdfdfc');
        svg.appendChild(svgNode('path', { d: 'M' + (x + 57) + ' ' + (y + 34) + 'l7 -8 7 8 M' + (x + 57) + ' ' + (y + 82) + 'l7 8 7 -8', fill: 'none', stroke: ink, 'stroke-width': 1.5 }));
        text(x + 64, y + 59, 'my', 11);
        if (four) for (let i = 0; i < 4; i += 1) { svg.appendChild(svgNode('circle', { cx: x + 12, cy: y + 23 + i * 26, r: 3, fill: '#c5c5c1', stroke: edge })); label(prefix + 'Button' + (i + 1), x - 45, y + 13 + i * 26, 44, 20, false); }
      }
      if (spec.layout === 'horizontal') {
        rect(18, 42, 424, 235, 6);
        ['Left', 'Right'].forEach((side, i) => { const x = 54 + i * 218; label(side + 'Top', x - 22, 58, 172, 24, false); face(x, 96, side, true); label(side + 'Bottom', x - 22, 238, 172, 23, false); });
      } else if (spec.layout === 'vertical') {
        rect(49, 14, 222, 430, 6); label('Top', 72, 25, 176, 24, false); face(96, 57, 'Upper', true); label('Middle', 72, 204, 176, 24, false); face(96, 245, 'Lower', true); label('Bottom', 72, 402, 176, 24, false);
      } else {
        rect(45, 98, 230, 250, 6); label('Top', 72, 112, 176, 24, false); face(96, 153, '', spec.layout === 'single4'); label('Bottom', 72, 311, 176, 24, false);
      }
    }
    container.appendChild(svg);
  }

  class Builder {
    constructor(root, data) {
      this.root = root; this.data = data; this.form = null; this.cfg = null; this.variant = null; this.modal = null;
      this.trigger = root.querySelector('[data-smd-trigger]');
      if (this.trigger) this.trigger.addEventListener('click', () => this.open());
      this.scope = root.closest('[id^="shopify-section-"]') || root.parentElement;
      if (data.product_form_id) this.form = document.getElementById(data.product_form_id);
      if (!this.form && data.variants.length) {
        const forms = this.scope.querySelectorAll('form[action*="/cart/add"]');
        if (forms.length === 1) this.form = forms[0];
      }
      if (this.form) {
        this.form.addEventListener('change', () => this.select());
        this.observer = new MutationObserver(() => this.select());
        this.observer.observe(this.form, { subtree: true, attributes: true, attributeFilter: ['value'] });
      } else if (data.variants.length) console.info('[SMD] Product form missing or ambiguous; pass product_form_id.');
      this.select();
    }
    select() {
      if (!this.trigger) return;
      const id = this.form && new FormData(this.form).get('id');
      this.variant = this.data.variants.find(v => String(v.id) === String(id));
      this.cfg = this.variant && configFor(this.data.catalog, this.variant.sku);
      this.trigger.hidden = !(this.form && this.cfg && this.variant.available && resolveFee(this.data, this.cfg));
    }
    async open(item, opener) {
      if (active) active.close(true);
      this.editItem = item || null;
      if (item) {
        this.variant = { id: item.variant_id || item.id, sku: item.properties._sku, price: item.original_price || item.price, available: true };
        this.cfg = configFor(this.data.catalog, this.variant.sku);
      } else this.select();
      if (!this.cfg || !this.variant || !resolveFee(this.data, this.cfg)) return;
      this.state = initialState(this.cfg); this.files = {}; this.oldFileURLs = {}; this.busy = false; this.pendingFiles = 0;
      if (item) {
        try {
          const saved = JSON.parse(item.properties._engraving_spec);
          if (saved.version !== 1 || saved.sku !== this.cfg.sku) throw new Error('Version mismatch');
          this.state = { layout: saved.layout, color: saved.color, style: saved.style, fields: saved.fields, context: saved.context || {} };
          Object.keys(item.properties).filter(k => k.indexOf('Logo File') === 0).forEach(k => { this.oldFileURLs[k] = item.properties[k]; });
        } catch (e) { notice('This cart item cannot be edited. Remove it and customize it again.'); return; }
      }
      this.opener = opener || document.activeElement; serial += 1; this.uid = 'smd-' + serial;
      const template = this.root.querySelector('template[data-smd-modal]');
      if (!template) return;
      this.modal = template.content.firstElementChild.cloneNode(true); document.body.appendChild(this.modal); active = this;
      this.dialog = this.modal.querySelector('[role="dialog"]');
      const title = this.modal.querySelector('[data-smd-title]'); title.id = this.uid + '-title'; this.dialog.setAttribute('aria-labelledby', title.id);
      this.modal.querySelector('[data-smd-product]').textContent = this.cfg.name;
      this.preview = this.modal.querySelector('[data-smd-preview]'); this.fieldsBox = this.modal.querySelector('[data-smd-fields]');
      this.errorBox = this.modal.querySelector('[data-smd-errors]');
      this.quantity = this.modal.querySelector('[data-smd-quantity]'); this.quantity.max = this.data.catalog.max_quantity;
      const productQuantity = this.form ? Number(new FormData(this.form).get('quantity') || 1) : 1;
      this.quantity.value = item ? item.quantity : (Number.isInteger(productQuantity) && productQuantity > 0 ? productQuantity : 1);
      this.quantity.addEventListener('input', () => this.update());
      this.submit = this.modal.querySelector('[data-smd-submit]'); this.submit.textContent = item ? 'Update Cart' : 'Add to Cart';
      this.modal.querySelectorAll('[data-smd-close]').forEach(n => n.addEventListener('click', () => this.close()));
      this.modal.addEventListener('click', e => { if (e.target === this.modal) this.close(); });
      this.keyHandler = e => this.keydown(e); document.addEventListener('keydown', this.keyHandler, true);
      this.modal.querySelector('[data-smd-reset]').addEventListener('click', () => {
        this.state = initialState(this.cfg); this.files = {}; this.oldFileURLs = {}; this.renderControls(); this.update();
      });
      this.modal.querySelector('form').addEventListener('submit', e => { e.preventDefault(); this.save(); });
      // Make background regions inert and preserve pre-existing attributes on close.
      this.background = Array.from(document.body.children).filter(n => n !== this.modal && !['SCRIPT', 'STYLE', 'LINK'].includes(n.tagName)).map(n => ({ node: n, inert: n.hasAttribute('inert'), hidden: n.getAttribute('aria-hidden') }));
      this.background.forEach(v => { v.node.setAttribute('inert', ''); v.node.setAttribute('aria-hidden', 'true'); });
      this.previousOverflow = document.body.style.overflow; document.body.style.overflow = 'hidden';
      this.renderControls(); this.update(); this.dialog.focus();
    }
    renderControls() {
      const controls = this.modal.querySelector('[data-smd-controls]'); controls.textContent = '';
      if (this.cfg.family === 'smoove' && this.cfg.layouts.length > 1) {
        const select = el('select', { 'aria-label': 'Frame layout' });
        const labels = { single1: 'Single frame, 1 channel', single4: 'Single frame, 4 channels', vertical: 'Double frame, vertical', horizontal: 'Double frame, horizontal' };
        this.cfg.layouts.forEach(v => select.appendChild(el('option', { value: v }, labels[v]))); select.value = this.state.layout;
        inputLabel(controls, 'Frame layout', select);
        select.addEventListener('change', () => { this.state.layout = select.value; this.state.fields = {}; this.files = {}; this.oldFileURLs = {}; fieldsFor(this.cfg, select.value).forEach(f => { this.state.fields[f.key] = { mode: 'text', text: f.default }; }); this.renderFields(); this.update(); });
      }
      const color = el('select', { 'aria-label': 'Button color' });
      let choices = [{ color: this.cfg.color, sku: this.cfg.sku }];
      if (!this.editItem && !this.cfg.white_only && this.cfg.color_skus) choices = Object.keys(this.cfg.color_skus).map(k => ({ color: k, sku: this.cfg.color_skus[k] })).filter(v => this.data.variants.some(i => i.sku === v.sku && i.available) && configFor(this.data.catalog, v.sku));
      choices.forEach(v => color.appendChild(el('option', { value: v.sku }, v.color))); color.value = this.cfg.sku;
      inputLabel(controls, this.cfg.family === 'smoove' ? 'Frame color' : 'Button color', color);
      color.addEventListener('change', () => {
        this.cfg = configFor(this.data.catalog, color.value); this.variant = this.data.variants.find(v => v.sku === color.value); this.state.color = this.cfg.color;
        this.modal.querySelector('[data-smd-product]').textContent = this.cfg.name; this.update();
      });
      const styles = el('fieldset', { class: 'smd-styles' }); styles.appendChild(el('legend', {}, 'Character style'));
      [['caps', 'ALL LETTERS CAPITALIZED'], ['title', 'First Letters Capitalized'], ['written', 'As Written']].forEach(pair => {
        const label = el('label'); const radio = el('input', { type: 'radio', name: this.uid + '-style', value: pair[0], 'aria-label': pair[1] }); radio.checked = this.state.style === pair[0];
        label.appendChild(radio); label.appendChild(el('span', {}, pair[1])); styles.appendChild(label);
        radio.addEventListener('change', () => {
          const old = this.state.style; this.state.style = radio.value;
          const spec = makeSpec(this.cfg, this.state);
          if (fieldsFor(this.cfg, this.state.layout).some(f => spec.fields[f.key].mode === 'text' && length(spec.fields[f.key].text) > f.max)) {
            this.state.style = old; styles.querySelector('input[value="' + old + '"]').checked = true; this.error('That style exceeds a character limit. Shorten the label first.');
          } else this.error('');
          this.update();
        });
      }); controls.appendChild(styles);
      const details = this.modal.querySelector('[data-smd-context]'); details.textContent = '';
      ['Project', 'Area', 'Room', 'Installer', 'Purchase Order'].forEach(k => {
        const field = el('input', { type: 'text', maxlength: 100, 'aria-label': k, autocomplete: 'off' }); field.value = this.state.context[k] || '';
        inputLabel(details, k, field); field.addEventListener('input', () => { this.state.context[k] = field.value; });
      });
      this.renderFields();
    }
    renderFields() {
      this.fieldsBox.textContent = ''; this.fieldNodes = {};
      fieldsFor(this.cfg, this.state.layout).forEach(f => {
        const value = this.state.fields[f.key] || { mode: 'text', text: f.default }; this.state.fields[f.key] = value;
        const row = el('div', { class: 'smd-field' });
        const head = el('div', { class: 'smd-field-head' }); const id = this.uid + '-' + f.key;
        head.appendChild(el('label', { for: id }, f.label));
        const counter = el('span', { id: id + '-count', class: 'smd-counter' }); head.appendChild(counter); row.appendChild(head);
        if (f.logo) {
          const mode = el('select', { 'aria-label': f.label + ' content type', class: 'smd-mode' });
          mode.appendChild(el('option', { value: 'text' }, 'Text')); mode.appendChild(el('option', { value: 'logo' }, 'Logo (.dxf)')); mode.value = value.mode;
          row.appendChild(mode); mode.addEventListener('change', () => { this.state.fields[f.key] = { mode: mode.value, text: '' }; delete this.files[f.key]; this.renderFields(); this.update(); this.fieldsBox.querySelector('[id="' + id + '"]').focus(); });
        }
        if (value.mode === 'logo') {
          const file = el('input', { id: id, type: 'file', accept: '.dxf', name: 'properties[Logo File - ' + f.label + ']', 'aria-label': f.label + ' DXF logo', 'aria-describedby': id + '-help' });
          row.appendChild(file);
          const help = el('p', { class: 'smd-help', id: id + '-help' }, 'DXF only. Maximum artwork: 1.97 in (5 cm) wide × 0.39 in (1 cm) high. Maximum file size: 5 MB. Your file is attached to the order; no email is needed.'); row.appendChild(help);
          if (value.file) row.appendChild(el('p', { class: 'smd-help' }, 'Selected: ' + value.file.name));
          if (this.editItem) { file.disabled = true; row.appendChild(el('p', { class: 'smd-help' }, 'The existing logo stays attached. To replace or add a logo file, remove this item and customize it again from the product page.')); }
          file.addEventListener('change', async () => {
            const chosen = file.files[0]; if (!chosen) return;
            if (!/\.dxf$/i.test(chosen.name) || chosen.size > this.data.catalog.max_logo_bytes || chosen.size === 0) { file.value = ''; this.error('Select a nonempty .dxf file no larger than 5 MB.'); return; }
            const uploadModal = this.modal; this.pendingFiles += 1; this.update();
            try {
              const sha = await digest(await readFile(chosen));
              if (this.modal !== uploadModal || this.state.fields[f.key] !== value) return;
              value.file = { name: chosen.name, sha256: sha, property: 'Logo File - ' + f.label };
              this.files[f.key] = chosen; this.error(''); this.renderFields(); this.update();
            } catch (e) { this.error(e.message); }
            finally { if (this.modal === uploadModal) { this.pendingFiles -= 1; this.update(); } }
          });
        } else {
          const input = el('input', { id: id, type: 'text', autocomplete: 'off', 'aria-label': f.label, 'aria-describedby': id + '-count', placeholder: f.default || 'Enter label' });
          input.value = value.text || ''; input.disabled = !!f.locked; row.appendChild(input);
          let accepted = input.value;
          input.addEventListener('beforeinput', e => {
            if (e.data === null || e.isComposing) return;
            const next = input.value.slice(0, input.selectionStart) + e.data + input.value.slice(input.selectionEnd);
            if (length(styled(next, this.state.style)) > f.max) { e.preventDefault(); this.error(f.label + ': maximum ' + f.max + ' characters, including spaces.'); }
          });
          input.addEventListener('input', e => {
            if (e.isComposing) return;
            if (length(styled(input.value, this.state.style)) > f.max || /[\u0000-\u001f\u007f]/.test(input.value)) {
              input.value = accepted; this.error(f.label + ': input rejected. Maximum ' + f.max + ' characters on one line.');
            } else { accepted = input.value; value.text = input.value; this.error(''); }
            this.update();
          });
          input.addEventListener('compositionend', () => input.dispatchEvent(new Event('input', { bubbles: true })));
          if (f.locked) row.appendChild(el('p', { class: 'smd-help' }, f.key === 'F' ? 'Factory STOP / MY command. This label stays unchanged.' : 'Factory arrow. This control is not listed for custom engraving on this form.'));
          else if (/[GH]/.test(f.key) && this.cfg.family === 'decoflex') row.appendChild(el('p', { class: 'smd-help' }, 'Leave blank for the factory arrow, or enter a command.'));
        }
        this.fieldNodes[f.key] = { counter: counter, definition: f }; this.fieldsBox.appendChild(row);
      });
    }
    update() {
      if (!this.modal) return;
      const spec = makeSpec(this.cfg, this.state), units = billable(this.cfg, spec), q = Number(this.quantity.value), fee = resolveFee(this.data, this.cfg);
      const quantity = Number.isInteger(q) && q > 0 ? q : 0;
      const money = cents => new Intl.NumberFormat(undefined, { style: 'currency', currency: this.data.currency }).format(cents / 100);
      const cost = units * quantity * Math.round(this.cfg.pricing.unit_price * 100);
      this.modal.querySelector('[data-smd-fee]').textContent = this.cfg.pricing.mode === 'flat' ? 'Custom engraving, ' + money(cost) : units + ' button' + (units === 1 ? '' : 's') + ' engraved' + (quantity > 1 ? ' × ' + quantity + ' keypads' : '') + ', ' + money(cost);
      this.modal.querySelector('[data-smd-total]').textContent = money(Number(this.variant.price) * quantity + cost);
      this.modal.querySelector('[data-smd-base]').textContent = 'Product: ' + money(Number(this.variant.price) * quantity);
      this.modal.querySelector('[data-smd-preview-note]').textContent = spec.color + ' · ' + (this.cfg.family === 'decoflex' ? this.cfg.type : 'Pure frame');
      Object.keys(this.fieldNodes).forEach(k => { const n = this.fieldNodes[k], v = spec.fields[k]; const used = v.mode === 'logo' ? 0 : length(v.text); n.counter.textContent = v.mode === 'logo' ? 'DXF' : used + ' / ' + n.definition.max; n.counter.classList.toggle('is-limit', used >= n.definition.max); });
      drawPreview(this.preview, this.cfg, spec);
      this.submit.disabled = this.busy || this.pendingFiles > 0 || !fee;
      this.modal.querySelector('.smd-logo-consent').style.display = Object.keys(spec.fields).some(k => spec.fields[k].mode === 'logo') ? 'flex' : 'none';
    }
    error(message) { if (this.errorBox) { this.errorBox.textContent = message; this.errorBox.hidden = !message; } }
    keydown(event) {
      if (event.key === 'Escape') { event.preventDefault(); this.close(); return; }
      if (event.key !== 'Tab' || !this.modal) return;
      const focusable = Array.from(this.modal.querySelectorAll('button,input,select,textarea,a[href],[tabindex="0"]')).filter(n => !n.disabled && n.getClientRects().length);
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (!first) { event.preventDefault(); this.dialog.focus(); return; }
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.dialog)) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || document.activeElement === this.dialog)) { event.preventDefault(); first.focus(); }
    }
    close(force) {
      if (this.busy && !force) return;
      if (!this.modal) return;
      document.removeEventListener('keydown', this.keyHandler, true);
      this.background.forEach(v => { if (!v.inert) v.node.removeAttribute('inert'); if (v.hidden === null) v.node.removeAttribute('aria-hidden'); else v.node.setAttribute('aria-hidden', v.hidden); });
      document.body.style.overflow = this.previousOverflow; this.modal.remove(); this.modal = null; active = null;
      if (this.opener && this.opener.isConnected) this.opener.focus();
    }
    async save() {
      if (this.busy || this.pendingFiles > 0) return;
      const spec = makeSpec(this.cfg, this.state), quantity = Number(this.quantity.value), fee = resolveFee(this.data, this.cfg);
      const errors = validate(this.cfg, spec, quantity, this.data.catalog.max_quantity);
      if (!this.modal.querySelector('[data-smd-consent]').checked) errors.push('Confirm that you have reviewed the engraving and understand custom items cannot be cancelled.');
      if (Object.keys(spec.fields).some(k => spec.fields[k].mode === 'logo') && !this.modal.querySelector('[data-smd-logo-consent]').checked) errors.push('Confirm that your logo artwork fits the stated size limit.');
      if (!fee) errors.push('The engraving fee is unavailable. Please try again later.');
      Object.keys(spec.fields).forEach(k => { const v = spec.fields[k]; if (v.mode === 'logo' && v.file && !this.files[k] && !this.oldFileURLs[v.file.property]) errors.push('Select the logo file for ' + k + ' on the product page.'); });
      if (errors.length) { this.error(errors.join(' ')); this.errorBox.focus(); return; }
      this.busy = true; this.update(); this.submit.textContent = 'Updating cart…'; this.error('');
      try {
        await enqueue(async () => {
          const ref = 'smd1-' + await digest(new TextEncoder().encode(canonical({ variant: String(this.variant.id), spec: spec })));
          const properties = this.editItem ? Object.assign({}, this.editItem.properties) : {};
          // /cart/change replaces all properties. Keep unrelated custom properties, replace only ours.
          Object.keys(properties).forEach(k => { if (/^(_engraving_|_sku$|Button [A-H]$|Frame |Left Frame |Right Frame |(?:Upper |Lower |Left |Right )?Button [1-4]$|Logo File|Button Color$|Character Style$|Project$|Area$|Room$|Installer$|Purchase Order$)/.test(k)) delete properties[k]; });
          fieldsFor(this.cfg, spec.layout).forEach(f => {
            const v = spec.fields[f.key];
            if (v.mode === 'text' && v.text) properties[f.label] = v.text;
            if (v.mode === 'logo' && v.file) { properties[f.label] = 'Logo: ' + v.file.name; if (this.oldFileURLs[v.file.property]) properties[v.file.property] = this.oldFileURLs[v.file.property]; }
          });
          Object.assign(properties, spec.context, { 'Button Color': spec.color, 'Character Style': STYLES[spec.style], _sku: spec.sku, _engraving_form: spec.form, _engraving_spec: canonical(spec), _engraving_version: '1', _engraving_ref: ref, _engraving_role: 'parent' });
          if (this.editItem) {
            const latest = await api('cart.js');
            if (!latest.items.some(i => i.key === this.editItem.key)) throw new Error('This cart line changed. Close the builder and reopen Edit engraving from the refreshed cart.');
            await api('cart/change.js', { id: this.editItem.key, quantity: quantity, properties: properties });
          } else {
            const items = [ { id: this.variant.id, quantity: quantity, properties: properties }, { id: fee.id, quantity: billable(this.cfg, spec) * quantity, properties: feeProperties(ref, spec.sku, this.variant.id) } ];
            const logoKeys = Object.keys(spec.fields).filter(k => spec.fields[k].mode === 'logo');
            if (logoKeys.length) {
              const data = new FormData();
              items.forEach((item, i) => { data.append('items[' + i + '][id]', item.id); data.append('items[' + i + '][quantity]', item.quantity); Object.keys(item.properties).forEach(k => data.append('items[' + i + '][properties][' + k + ']', item.properties[k])); });
              logoKeys.forEach(k => data.append('items[0][properties][' + spec.fields[k].file.property + ']', this.files[k], this.files[k].name));
              await api('cart/add.js', data);
            } else await api('cart/add.js', { items: items });
          }
          const cart = await reconcile(); this.busy = false; this.close(); await refresh(cart, true);
        });
      } catch (e) {
        // Do not automatically retry an uncertain add. It may already have succeeded server-side.
        this.error(e.message + ' Check your cart before adding again.');
        notice('A cart update needs review. Open the cart and check the product and engraving fee before retrying.');
        const link = el('a', { href: url('cart'), class: 'smd-review-cart' }, 'Review cart');
        if (this.errorBox) this.errorBox.appendChild(link);
      } finally { this.busy = false; if (this.modal) { this.submit.textContent = this.editItem ? 'Update Cart' : 'Add to Cart'; this.update(); } }
    }
  }
  function init() {
    document.querySelectorAll('[data-smd-engraving-root]').forEach(root => {
      if (root.dataset.smdReady) return;
      try {
        const data = JSON.parse(root.querySelector('[data-smd-config]').textContent);
        root.dataset.smdReady = 'true'; const builder = new Builder(root, data); roots.add(builder);
        if (!runtime) { runtime = data; installGuard(); scheduleSync(); }
      } catch (e) { console.warn('[SMD] Engraving setup unavailable.', e); }
    });
  }
  async function edit(key, opener) {
    try {
      await queue;
      const cart = await api('cart.js'), item = cart.items.find(i => i.key === key);
      if (!item || !item.properties || item.properties._engraving_role !== 'parent') throw new Error('This engraved cart item is no longer available. Refresh the cart.');
      const builder = Array.from(roots).find(b => configFor(b.data.catalog, item.properties._sku));
      if (builder) await builder.open(item, opener); else throw new Error('Load the engraving snippet on the cart page to edit this item.');
    } catch (e) { notice(e.message); }
  }
  const apiPublic = Object.assign({}, core, { init: init, edit: edit, syncCart: () => enqueue(reconcile), cartAdapter: null, isBusy: () => pending > 0 });
  global.SMDEngraving = apiPublic;
  document.addEventListener('click', e => { const target = e.target.closest('[data-smd-edit-key]'); if (target) { e.preventDefault(); edit(target.dataset.smdEditKey, target); } });
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', e => { roots.forEach(b => { if (e.target.contains(b.root)) { if (b.observer) b.observer.disconnect(); if (active === b) b.close(true); roots.delete(b); } }); });
  document.addEventListener('variant:change', () => roots.forEach(b => b.select()));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})(typeof window !== 'undefined' ? window : (typeof global !== 'undefined' ? global : this));
